/**
 * estadoFila.ts — una sola clasificación del estado de la fila (P2VOC-a, 8-oct-2026)
 *
 * OBS-UI-P2-AVISO-SIN-GEOLOCALIZAR-Y-BARRA-DE-ESTADO-NO-CUENTAN-LO-MISMO.
 * Hasta aquí cada cuenta del asistente clasificaba la fila a su manera (la
 * leyenda de P2 por la marca de la validación cruzada, la barra y los filtros
 * por «tiene X e Y»). Esta función es la fuente única: toda cuenta o rótulo de
 * estado debe leer de ella.
 *
 * Tres estados, sellados por Luis (UIDIAG y P2VOC, 8-oct-2026):
 *  - `sin_coordenada`: falta X o Y (ausente o 0). Manda sobre todo lo demás.
 *  - `ubicada`: tiene coordenada y nada que revisar, o la confirmó el técnico.
 *  - `por_revisar`: tiene coordenada sin confirmar y es revisión manual, baja
 *    confianza o centroide municipal (el centroide no es la ubicación del
 *    elemento).
 *
 * Funciones puras, sin estado ni efectos.
 */
import { esOrigenCentroide } from '../../../utils/procedenciaVia';

export type EstadoUbicacionFila = 'ubicada' | 'por_revisar' | 'sin_coordenada';

/** Lo mínimo que hace falta de una fila para clasificarla. */
export interface FilaClasificable {
  xFinal?: number | null;
  yFinal?: number | null;
  confirmado?: boolean;
  source?: string;
  crossValidation?: { flag?: string } | null;
}

export const ETIQUETA_ESTADO_FILA: Record<EstadoUbicacionFila, string> = {
  ubicada: 'Ubicada',
  por_revisar: 'Por revisar',
  sin_coordenada: 'Sin coordenada',
};

/** Marcas de la validación cruzada que piden que el técnico mire la fila. */
const MARCAS_POR_REVISAR: ReadonlySet<string> = new Set([
  'REVISION_MANUAL',
  'BAJA_CONFIANZA',
  'SIN_COORDENADAS',
]);

function tieneCoordenada(fila: FilaClasificable): boolean {
  return (
    typeof fila.xFinal === 'number' && Number.isFinite(fila.xFinal) &&
    typeof fila.yFinal === 'number' && Number.isFinite(fila.yFinal) &&
    fila.xFinal !== 0 &&
    fila.yFinal !== 0
  );
}

export function clasificarEstadoFila(fila: FilaClasificable): EstadoUbicacionFila {
  if (!tieneCoordenada(fila)) return 'sin_coordenada';
  if (fila.confirmado === true) return 'ubicada';
  const marca = fila.crossValidation?.flag;
  if (marca !== undefined && MARCAS_POR_REVISAR.has(marca)) return 'por_revisar';
  if (esOrigenCentroide(fila.source)) return 'por_revisar';
  return 'ubicada';
}

export function contarEstadosFila(
  filas: readonly FilaClasificable[],
): Record<EstadoUbicacionFila, number> {
  const cuenta: Record<EstadoUbicacionFila, number> = { ubicada: 0, por_revisar: 0, sin_coordenada: 0 };
  for (const fila of filas) cuenta[clasificarEstadoFila(fila)] += 1;
  return cuenta;
}
